/**
 * Imperative helpers for editing the 10-Week Roadmap inside its Shadow DOM.
 *
 * The roadmap is a `.timeline` of `.row`s; each row is a grid
 * (`116px repeat(10,1fr)`) where column 1 is the lane label and columns 2..11
 * are weeks W0..W9. Bars carry an inline `grid-column: start / end` and a colour
 * class. This module parses/writes that geometry, injects the edit-mode chrome
 * (row add/remove, add-bar), and serializes back to clean HTML.
 */
export const WEEKS = 10 // W0..W9 -> grid columns 2..11

export const BAR_TYPES = [
  { key: "p1", label: "Priority (blue)" },
  { key: "sec", label: "Backlog (slate)" },
  { key: "enh", label: "Enhancement (green)" },
  { key: "jur", label: "Strategic (plum)" },
  { key: "find", label: "Findings (amber)" },
  { key: "dlv", label: "Deliverable (violet)" },
  { key: "go", label: "Go-live (solid plum)" },
  { key: "ghost", label: "Spacer (invisible)" },
] as const
export type BarType = (typeof BAR_TYPES)[number]["key"]
const TYPE_KEYS = BAR_TYPES.map((t) => t.key)

const CHROME = ".rm-addrow,.rm-delrow,.rm-addbar"

/** Turn the bundled roadmap CSS into Shadow-DOM-scoped, light-pinned CSS:
 *  :root/body → :host, drop the dark + [data-theme] blocks. */
export function shadowCss(rawHtml: string): string {
  let css = /<style>([\s\S]*?)<\/style>/.exec(rawHtml)?.[1] ?? ""
  css = css.replace(/@media \(prefers-color-scheme:dark\)\{:root\{[\s\S]*?\}\}/g, "")
  css = css.replace(/:root\[data-theme="dark"\]\{[\s\S]*?\}/g, "")
  css = css.replace(/:root\[data-theme="light"\]\{[\s\S]*?\}/g, "")
  css = css.replace(/:root\{/, ":host{") // the remaining (light default) block
  css = css.replace(/\bbody\{/, ":host{display:block;")
  css +=
    "\n:host(.editing) [contenteditable=\"true\"]{outline:1px dashed var(--accent);outline-offset:2px;border-radius:3px;cursor:text;}" +
    "\n:host(.editing) [contenteditable=\"true\"]:hover{background:var(--accent-soft);}" +
    "\n:host(.editing) .bar:not(.ghost){cursor:pointer;}" +
    "\n:host(.editing) .bar.ghost{outline:1px dashed var(--rule-2);min-height:16px;}" +
    "\n:host(.editing) .timeline .bar:not(.ghost):hover{outline:2px solid var(--accent);outline-offset:1px;}" +
    "\n:host(.editing) .vac{cursor:pointer;}" +
    "\n:host(.editing) .vac:hover{outline:2px solid var(--accent);outline-offset:1px;}" +
    "\n.rm-addrow,.rm-delrow,.rm-addbar{font-family:var(--sans);font-size:9px;border-radius:4px;cursor:pointer;border:1px solid var(--rule);background:var(--panel);color:var(--muted);line-height:1;}" +
    "\n.rm-addbar{padding:2px 6px;}.rm-addrow{padding:2px 8px;margin:2px auto;}" +
    "\n.rm-delrow{padding:0 4px;border:none;background:transparent;color:var(--crit);font-size:13px;}" +
    "\n:host(.editing) .row{position:relative;}" +
    "\n.rm-delrow{position:absolute;left:-14px;top:50%;transform:translateY(-50%);}" +
    "\n.rm-addrow:hover,.rm-addbar:hover{background:var(--accent-soft);color:var(--accent);}"
  return css
}

export interface BarProps {
  html: string
  startWeek: number // 0..9
  endWeek: number // 0..9 inclusive
  type: BarType
}

const clampWeek = (n: number) => Math.max(0, Math.min(WEEKS - 1, n))

export function parseBar(el: Element): BarProps {
  const style = el.getAttribute("style") ?? ""
  const m = /grid-column:\s*(\d+)\s*\/\s*(\d+)/.exec(style)
  const colStart = m ? Number(m[1]) : 2
  const colEnd = m ? Number(m[2]) : 3
  const type = (TYPE_KEYS.find((t) => el.classList.contains(t)) ?? "p1") as BarType
  return {
    html: el.innerHTML,
    startWeek: clampWeek(colStart - 2),
    endWeek: clampWeek(colEnd - 3),
    type,
  }
}

/** Preserve any classes that aren't a bar-type token (e.g. obj, endoct1). */
function keptClasses(el: Element): string[] {
  return [...el.classList].filter((c) => c !== "bar" && !TYPE_KEYS.includes(c as BarType))
}

export function applyBar(el: Element, p: BarProps): void {
  const s = clampWeek(p.startWeek)
  const e = Math.max(s, clampWeek(p.endWeek))
  el.className = ["bar", p.type, ...keptClasses(el)].join(" ")
  el.setAttribute("style", `grid-column:${2 + s} / ${3 + e}`)
  el.innerHTML = p.html
}

// ---- key-vacation chips: date-driven positioning -------------------------
const DAY_MS = 86_400_000
// W0 Monday of the roadmap window (10 Aug 2026 → 16 Oct 2026, weeks W0..W9).
const VAC_EPOCH = new Date(2026, 7, 10)
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const MON_IDX: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
}

export interface VacProps {
  name: string
  start: string // ISO (YYYY-MM-DD)
  end: string
  note: string
}

function mondayOf(d: Date): Date {
  const day = d.getDay()
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + (day === 0 ? -6 : 1 - day))
}

/** The roadmap week column (0..9) that contains an ISO date. */
export function weekOfDate(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number)
  if (!y) return 0
  const mon = mondayOf(new Date(y, m - 1, d))
  const idx = Math.round((mon.getTime() - VAC_EPOCH.getTime()) / (7 * DAY_MS))
  return Math.max(0, Math.min(WEEKS - 1, idx))
}

function fmtRange(startIso: string, endIso: string, note: string): string {
  const [, sm, sd] = startIso.split("-").map(Number)
  const [, em, ed] = endIso.split("-").map(Number)
  const range =
    sm === em ? `${sd}–${ed} ${MON[em - 1]}` : `${sd} ${MON[sm - 1]} – ${ed} ${MON[em - 1]}`
  return note ? `${range} · ${note}` : range
}

/** Fallback for seed chips that predate data-start/data-end: parse the display
 *  text ("20–21 Aug", "21 Aug – 18 Sep · …"). Year is the roadmap's (2026). */
function parseVacText(text: string): { start: string; end: string; note: string } {
  let note = ""
  let dp = text
  const dot = text.indexOf("·")
  if (dot >= 0) {
    note = text.slice(dot + 1).trim()
    dp = text.slice(0, dot).trim()
  }
  const mi = (s: string) => MON_IDX[s.slice(0, 3).toLowerCase()] ?? 1
  const iso = (mo: number, d: number) => `2026-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`
  let m = /(\d+)\s+([A-Za-z]{3,})\s*[–—-]\s*(\d+)\s+([A-Za-z]{3,})/.exec(dp)
  if (m) return { start: iso(mi(m[2]), +m[1]), end: iso(mi(m[4]), +m[3]), note }
  m = /(\d+)\s*[–—-]\s*(\d+)\s+([A-Za-z]{3,})/.exec(dp)
  if (m) return { start: iso(mi(m[3]), +m[1]), end: iso(mi(m[3]), +m[2]), note }
  return { start: "", end: "", note }
}

export function parseVac(el: Element): VacProps {
  const name = el.querySelector("b")?.textContent?.trim() ?? ""
  let start = el.getAttribute("data-start") ?? ""
  let end = el.getAttribute("data-end") ?? ""
  let note = el.getAttribute("data-note") ?? ""
  if (!start || !end) {
    const p = parseVacText(el.querySelector("span")?.textContent ?? "")
    start = start || p.start
    end = end || p.end
    note = note || p.note
  }
  return { name, start: start || "2026-08-10", end: end || "2026-08-14", note }
}

/** Write a vacation chip: position it to the weeks its dates fall in, and pick
 *  the wide (`.vac.span`) layout when it spans more than one week. */
export function applyVac(el: Element, p: VacProps): void {
  const s = weekOfDate(p.start)
  const e = Math.max(s, weekOfDate(p.end))
  el.className = e > s ? "vac span" : "vac"
  el.setAttribute("style", `grid-column:${2 + s} / ${3 + e}`)
  el.setAttribute("data-start", p.start)
  el.setAttribute("data-end", p.end)
  if (p.note) el.setAttribute("data-note", p.note)
  else el.removeAttribute("data-note")
  const doc = el.ownerDocument
  el.textContent = ""
  const b = doc.createElement("b")
  b.textContent = p.name
  const span = doc.createElement("span")
  span.textContent = fmtRange(p.start, p.end, p.note)
  el.append(b, span)
}

export function addVac(rowEl: Element): Element {
  const el = rowEl.ownerDocument.createElement("div")
  applyVac(el, { name: "Name", start: "2026-08-10", end: "2026-08-14", note: "" })
  rowEl.appendChild(el)
  return el
}

export function addBar(rowEl: Element): Element {
  const bar = rowEl.ownerDocument.createElement("div")
  bar.className = "bar p1"
  bar.setAttribute("style", "grid-column:2 / 4")
  bar.textContent = "New bar"
  rowEl.appendChild(bar)
  return bar
}

/** Insert a fresh empty row (a lane cell + a full-width ghost) after `rowEl`. */
export function addRowAfter(rowEl: Element): Element {
  const doc = rowEl.ownerDocument
  const row = doc.createElement("div")
  row.className = "row"
  row.innerHTML = '<div class="lane"></div><div class="bar p1" style="grid-column:2 / 12">New row</div>'
  rowEl.after(row)
  return row
}

export function injectEditChrome(content: HTMLElement): void {
  content.querySelectorAll<HTMLElement>(".timeline > .row").forEach((row) => {
    if (!row.querySelector(":scope > .rm-delrow")) {
      const del = row.ownerDocument.createElement("button")
      del.type = "button"
      del.className = "rm-delrow"
      del.contentEditable = "false"
      del.textContent = "×"
      del.title = "Delete row"
      row.appendChild(del)
    }
    if (!row.querySelector(":scope > .rm-addbar")) {
      const add = row.ownerDocument.createElement("button")
      add.type = "button"
      add.className = "rm-addbar"
      add.contentEditable = "false"
      add.textContent = "+ bar"
      row.appendChild(add)
    }
  })
  // one "+ row" button at the end of the timeline
  const timeline = content.querySelector(".timeline")
  if (timeline && !content.querySelector(".rm-addrow")) {
    const add = content.ownerDocument.createElement("button")
    add.type = "button"
    add.className = "rm-addrow"
    add.contentEditable = "false"
    add.textContent = "+ row"
    timeline.appendChild(add)
  }
}

export function removeEditChrome(content: HTMLElement): void {
  content.querySelectorAll(CHROME).forEach((el) => el.remove())
}

function stripped(content: HTMLElement): HTMLElement {
  const clone = content.cloneNode(true) as HTMLElement
  clone.querySelectorAll(CHROME).forEach((el) => el.remove())
  clone.querySelectorAll("[contenteditable]").forEach((el) => el.removeAttribute("contenteditable"))
  return clone
}

/** Clone → drop chrome + contenteditable + the injected actual-delivery row →
 *  sanitized .sheet HTML (the actual-anchor is left empty for re-fill). */
export function serialize(content: HTMLElement, sanitize: (h: string) => string): string {
  const clone = stripped(content)
  const anchor = clone.querySelector('[data-anchor="actual"]')
  if (anchor) anchor.innerHTML = ""
  const sheet = clone.querySelector(".sheet")
  return sanitize(sheet ? sheet.outerHTML : clone.innerHTML)
}

/** .sheet HTML for the print/PDF iframe: chrome-free, but the computed
 *  actual-delivery row is kept (so the export shows it). */
export function exportSheetHtml(content: HTMLElement): string {
  const clone = stripped(content)
  const sheet = clone.querySelector(".sheet")
  return sheet ? sheet.outerHTML : clone.innerHTML
}
